import React from "react";
import { TagIcon } from "../../utils/tagIcons";
import { timeAgo } from "../../utils/relativeTime";
import { useSiteAlerts } from "./SiteAlertsContext";
import { familyOf, tintOf } from "./alertDisplay";
import "./SiteAlerts.css";

/**
 * Floating bottom-right alert stack — the quieter style for announcements
 * and info. Renders every active alert with display_style === 'card'.
 * Unlike a toast these never auto-fade: a card stays until the user
 * dismisses it (or, for non-dismissable ones, until an admin ends it).
 *
 * `children` are other cards that belong in the same column (today: lead
 * alerts, components/osr/OsrAlerts.js). One stack means one position, one
 * width, and one offset above the time clock bar, so cards from different
 * features can never cover each other. They sit above the announcements:
 * they ask for an action, an announcement does not.
 */
export default function SiteAlertCards({ children }) {
  const { alerts, dismiss } = useSiteAlerts();
  const cards = alerts.filter((a) => a.display_style === "card");
  if (cards.length === 0 && !children) return null;

  return (
    <div className="site-alert-cards" role="status" aria-live="polite">
      {children}
      {cards.map((alert) => {
        const family = familyOf(alert);
        return (
          <div
            key={alert.id}
            className="site-alert-card"
            style={{
              borderLeftColor: family.vivid,
              backgroundImage: `linear-gradient(${tintOf(family.vivid)}, ${tintOf(family.vivid)})`,
            }}
          >
            <span
              className="site-alert-card-icon"
              style={{ color: family.vivid }}
              aria-hidden="true"
            >
              <TagIcon name={family.icon} size={18} strokeWidth={2.2} />
            </span>
            <span className="site-alert-card-body">
              <span className="site-alert-card-text">{alert.message}</span>
              <span className="site-alert-card-time">
                Posted {timeAgo(alert.published_at)}
              </span>
            </span>
            {alert.dismissable && (
              <button
                type="button"
                className="site-alert-card-close"
                onClick={() => dismiss(alert.id)}
                aria-label="Dismiss alert"
                title="Dismiss"
              >
                ×
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
